// ========== React & Libraries ==========
import { useMemo } from 'react';
import { z } from 'zod';
import { ClipboardPaste, Info } from 'lucide-react';
import { Temporal } from 'temporal-polyfill';

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// ========== Commons ==========
import { CoachGrade, formatInstant, FrequentTimeZones, isAirAdmin, isNotNullish } from '@airreading/common';
import {
  CoachTqReviewItem,
  CoachTqReviewStatus,
  CoachTqReviewStatusLabelMap,
  CoachTqReviewOpenStatus,
  CoachTqReviewType,
  CoachTqReviewTypeLabelMap,
  GetCoachesTqReviewSortableColumn,
  coachOverallGradeFrom,
  coachTqReviewOpenStatusSchema,
  coachTqReviewTypeSchema,
  getCoachesTqReviewSortOrderSchema,
  getCoachesTqReviewSortableColumnSchema,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';

// ========== Utils & Helpers ==========
import { getCoachesTqReview, EmptyArray } from '@/lib/api-calls';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { usePaginatedFilters } from '@/lib/filtering';
import { useErrorToast } from '@/lib/error-handling';
import { paginationRequestSchema } from '@/lib/pagination';
import { airColumnHelper } from '@/lib/tables';
import { useSorting } from '@/lib/sorting';
import { cn } from '@/lib/tailwind-utils';
import { getCoachOverallGradeColor } from '@/lib/coach-utils';

// ========== Table Components ==========
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';

// ========== Layout / UI ==========
import { InfoPage } from '@/components/info-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AirIdDisplay } from '@/components/air-id-display';
import { ArrowTooltip } from '@/components/arrow-tooltip';
import { LinkToExternal } from '@/components/link-to-external';
import { StudentNeedsIndicators } from '@/components/student-needs-indicators';

// ========== Local Components ==========
import { coachesSectionTabs } from '../-components/tabs';
import { CreateTqReview } from '../-components/create-tq-review';
import { EditTqReviewStatus } from '../-components/edit-tq-review-status';
import { CoachTqReviewTypeBadgeClassMap } from '../-components/tq-review-type-badge';

const searchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  reviewTypes: z.array(coachTqReviewTypeSchema).optional(),
  statuses: z.array(coachTqReviewOpenStatusSchema).optional(),
  sortBy: getCoachesTqReviewSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/teachers/tq-review/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: TqReviewIndex,
  validateSearch: searchSchema,
});

const columnHelper = airColumnHelper<CoachTqReviewItem>(getCoachesTqReviewSortableColumnSchema);

const CoachTqReviewTypeAirtableFormUrlMap: Record<CoachTqReviewType, string> = {
  [CoachTqReviewType.QR_New_First]: 'https://airtable.com/appSaQO8clvMKRY2k/shrCqy2FW3JrSdKaW',
  [CoachTqReviewType.FR_New_Second]: 'https://airtable.com/appSaQO8clvMKRY2k/shrbdaCW6lB6n7BuF',
  [CoachTqReviewType.FR_Current]: 'https://airtable.com/appSaQO8clvMKRY2k/shrbdaCW6lB6n7BuF',
  [CoachTqReviewType.QR_Return]: 'https://airtable.com/appSaQO8clvMKRY2k/shrCqy2FW3JrSdKaW',
  [CoachTqReviewType.Assessment]: 'https://airtable.com/appSaQO8clvMKRY2k/shrzWnqcj2dSLMFiN',
  [CoachTqReviewType.PM_GLA]: 'https://airtable.com/appSaQO8clvMKRY2k/shrzWnqcj2dSLMFiN',
};

function TeachingGradeBadge({ grade }: { grade: CoachGrade | null | undefined }) {
  if (!grade) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }

  const color = getCoachOverallGradeColor(coachOverallGradeFrom(grade));
  if (!color) {
    return <span className="text-sm text-muted-foreground whitespace-nowrap">{grade.replaceAll('_', ' ')}</span>;
  }

  return (
    <span
      className="inline-flex items-center justify-center rounded-full text-white font-bold w-7 h-7 text-xl shrink-0"
      style={{ backgroundColor: color }}
    >
      {grade}
    </span>
  );
}

function formatReviewInstant(instant: Temporal.Instant | null | undefined, timeZone: string): string {
  if (!instant) return '—';
  return formatInstant(instant, timeZone).formattedDate;
}

const linkClassName = 'text-blue-600 hover:text-blue-800';

const tableColumns = [
  columnHelper.display({
    id: 'clipboardPaste',
    header: '',
    cell: ({ row }) => {
      const reviewTypeLabel = CoachTqReviewTypeLabelMap[row.original.reviewType];
      const formUrl = CoachTqReviewTypeAirtableFormUrlMap[row.original.reviewType];
      return (
        <a
          href={formUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex p-1 text-foreground hover:text-foreground/80"
          aria-label={`Open ${reviewTypeLabel} Airtable form`}
          title={`Open ${reviewTypeLabel} form`}
        >
          <ClipboardPaste size={16} />
        </a>
      );
    },
    size: 40,
  }),

  columnHelper.accessor('teacherName', {
    id: 'teacherName',
    header: 'Teacher name',
    cell: ({ row, getValue }) => (
      <Link to="/teachers/$teacherId" params={{ teacherId: row.original.coachId }} className={linkClassName}>
        {getValue() || '—'}
      </Link>
    ),
    enableSorting: false,
  }),

  columnHelper.accessor('teachingGrade', {
    id: 'teachingGrade',
    header: 'Teaching grade',
    cell: ({ getValue }) => <TeachingGradeBadge grade={getValue()} />,
    enableSorting: false,
  }),

  columnHelper.accessor('reviewType', {
    id: 'reviewType',
    header: 'Review type',
    cell: ({ getValue }) => {
      const reviewType = getValue();
      return (
        <Badge
          variant="outline"
          className={cn('rounded-full font-medium whitespace-nowrap', CoachTqReviewTypeBadgeClassMap[reviewType])}
        >
          {CoachTqReviewTypeLabelMap[reviewType]}
        </Badge>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('status', {
    id: 'status',
    header: 'Review status',
    cell: ({ row, getValue }) => {
      const status = getValue();
      return (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          {status === CoachTqReviewStatus.blank ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span>{CoachTqReviewStatusLabelMap[status]}</span>
          )}
          <EditTqReviewStatus review={row.original} />
        </div>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('lastReviewInstant', {
    id: 'lastReviewDate',
    header: 'Last review date',
    cell: ({ row, getValue }) => {
      const lastReviewInstant = getValue();
      const { historicalReviews } = row.original;
      const timeZone = FrequentTimeZones.UsCentral;

      return (
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          <span>{formatReviewInstant(lastReviewInstant, timeZone)}</span>
          {historicalReviews.length > 0 && (
            <ArrowTooltip
              position="bottom"
              trigger={
                <span className="inline-flex text-foreground" aria-label="Review history">
                  <Info className="size-3.5" />
                </span>
              }
            >
              <div className="flex flex-col gap-1 text-left">
                {historicalReviews.map((entry) => (
                  <div key={`${entry.reviewInstant.toString()}-${entry.reviewType}`}>
                    {formatReviewInstant(entry.reviewInstant, timeZone)}
                    {' · '}
                    {CoachTqReviewTypeLabelMap[entry.reviewType]}
                  </div>
                ))}
              </div>
            </ArrowTooltip>
          )}
        </div>
      );
    },
  }),

  columnHelper.accessor('mostRecentSessionInstant', {
    id: 'mostRecentSessionDate',
    header: 'Most recent session date',
    cell: ({ row, getValue }) => {
      const instant = getValue();
      const sessionId = row.original.mostRecentSessionId;
      if (!instant || !sessionId) return '—';
      return (
        <Link
          to="/sessions/$sessionId"
          params={{ sessionId }}
          search={{ from: 'past' }}
          className={cn(linkClassName, 'whitespace-nowrap')}
        >
          {formatReviewInstant(instant, FrequentTimeZones.UsCentral)}
        </Link>
      );
    },
  }),

  columnHelper.accessor('meetingType', {
    id: 'meetingType',
    header: 'Meeting type',
    cell: ({ getValue }) => {
      const meetingType = getValue();
      return meetingType ? VideoConferenceTypeLabels[meetingType] : '—';
    },
    enableSorting: false,
  }),

  columnHelper.accessor('recordingUrls', {
    id: 'recording',
    header: 'Recording',
    cell: ({ getValue }) => {
      const recordingUrls = getValue();
      if (recordingUrls.length === 0) return '—';
      return (
        <div className="flex flex-col gap-1 max-w-48">
          {recordingUrls.map((url, index) => (
            <LinkToExternal
              key={`${url}-${index}`}
              href={url}
              className="text-blue-600 hover:text-blue-800 truncate"
              title={url}
            >
              {url}
            </LinkToExternal>
          ))}
        </div>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('lessonName', {
    id: 'lesson',
    header: 'Lesson',
    cell: ({ row, getValue }) => {
      const lessonName = getValue();
      const lessonId = row.original.lessonId;
      if (!lessonName) return '—';
      if (!lessonId) return lessonName;
      return (
        <Link to="/lessons/$lessonId" params={{ lessonId }} className={cn(linkClassName, 'whitespace-nowrap')}>
          {lessonName}
        </Link>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      const students = getValue();
      if (students.length === 0) return '—';
      return (
        <div className="flex flex-wrap gap-1 min-w-40">
          {students.map((student) => (
            <Badge key={student.id} variant="outline" className="text-left rounded-sm px-1 w-fit">
              <span className="inline-flex items-center gap-1">
                <StudentNeedsIndicators
                  specialEd={student.specialEd}
                  dyslexic={student.dyslexic}
                  ell={student.ell}
                  mode="icons"
                />
                {student.name}
              </span>
            </Badge>
          ))}
        </div>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('classNotes', {
    id: 'classNotes',
    header: 'Class notes',
    cell: ({ getValue }) => {
      const classNotes = getValue();
      if (!classNotes) return '-';
      return (
        <ArrowTooltip
          position="bottom"
          trigger={<div className="max-w-72 whitespace-pre-wrap line-clamp-2 text-left">{classNotes}</div>}
        >
          <div className="max-w-md whitespace-pre-wrap text-left">{classNotes}</div>
        </ArrowTooltip>
      );
    },
    enableSorting: false,
  }),

  columnHelper.accessor('missingFeedbackCount', {
    id: 'missingFeedbackCount',
    header: 'Missing feedback (30 Days)',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),

  columnHelper.accessor('activeStudentGroupCount', {
    id: 'activeStudentGroups',
    header: 'Active student groups',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),

  columnHelper.accessor('totalFutureSessions', {
    id: 'totalFutureSessions',
    header: 'Total future sessions',
    cell: ({ getValue }) => getValue(),
    enableSorting: false,
  }),

  columnHelper.accessor('mostRecentSessionId', {
    id: 'sessionId',
    header: 'Session ID',
    cell: ({ getValue }) => {
      const sessionId = getValue();
      if (!sessionId) return '—';
      return <AirIdDisplay entityType="ses" rawId={sessionId} />;
    },
    enableSorting: false,
  }),

  columnHelper.accessor('reviewInstant', {
    id: 'reviewDate',
    header: 'Review date',
    cell: ({ getValue }) => formatReviewInstant(getValue(), FrequentTimeZones.UsCentral),
    enableSorting: false,
  }),
];

function TqReviewIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      reviewTypes: [[reviewTypes, activeReviewTypes], setReviewTypes],
      statuses: [[statuses, activeStatuses], setStatuses],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'reviewTypes', 'statuses']);

  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetCoachesTqReviewSortableColumn>([sortBy, didChangeSortBy]);

  const { data, isPending, error, refetch } = useQuery(
    getCoachesTqReview({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        reviewTypes: activeReviewTypes,
        statuses: activeStatuses,
      },
      sortBy,
    }),
  );

  const reviews = useMemo(() => data?.reviews ?? EmptyArray, [data]);

  useErrorToast(error, 'Error loading TQ review queue');

  const table = useReactTable({
    data: reviews,
    columns: tableColumns,
    state: {
      pagination: { pageIndex, pageSize },
      sorting,
    },
    getRowId: (row) => row.id,
    rowCount: data?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
  });

  const reviewTypeOptions = coachTqReviewTypeSchema.options.map((value) => ({
    label: CoachTqReviewTypeLabelMap[value],
    value,
    className: CoachTqReviewTypeBadgeClassMap[value],
  }));

  const reviewStatusOptions = coachTqReviewOpenStatusSchema.options.map((value) => ({
    label: CoachTqReviewStatusLabelMap[value],
    value,
  }));

  const selectedReviewType = reviewTypes?.[0];
  const selectedReviewStatus = statuses?.[0];
  const hasActiveFilters = !!activeNameQuery || !!selectedReviewType || !!selectedReviewStatus;

  const filterComponents = (
    <>
      <TableFilterInputField
        placeholder="Search name"
        value={nameQuery ?? ''}
        onChange={(e) => setNameQuery(e.target.value)}
      />
      <TableFilterDropdown
        title="Review type"
        singleSelect
        options={reviewTypeOptions}
        selectedValues={[selectedReviewType].filter(isNotNullish)}
        onChange={(selected) => {
          const next = selected.filter((value) => value !== selectedReviewType)[0] as CoachTqReviewType | undefined;
          setReviewTypes(next ? [next] : undefined);
        }}
      />
      <TableFilterDropdown
        title="Review status"
        singleSelect
        options={reviewStatusOptions}
        selectedValues={[selectedReviewStatus].filter(isNotNullish)}
        onChange={(selected) => {
          const next = selected.filter((value) => value !== selectedReviewStatus)[0] as
            | CoachTqReviewOpenStatus
            | undefined;
          setStatuses(next ? [next] : undefined);
        }}
        data-testid="teachers-tq-review-filter-dropdown-review-status"
      />
      <TableFilterResetButton show={hasActiveFilters} onClick={resetFilters} />
      <div className="!ml-auto">
        <CreateTqReview trigger={<Button>Add review</Button>} />
      </div>
    </>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        hasError={!!error}
        emptyState="No TQ reviews found"
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents: filterComponents }}
      />
    </InfoPage>
  );
}
